import {
  partsTableUrl,
  pnUrl,
  ffUrl,
  assetURL,
} from "../../../Experience/World/config";

import debug from "debug";

const log = debug("app:dataprovider");



/**
 * Singleton class responsible for managing and caching data related to groups, category tables, form factors, and parts.
 * @category Experience
 * @subcategory Utils
 */
class DataVault {
  static instance = null;



  /**
   * Creates an instance of DataVault.
   * @param {Object} experience - The experience object containing eventHub and dataProvider.
   */
  constructor(experience) {
    if (DataVault.instance) {
      return DataVault.instance;
    }
    this.experience = experience;
    this.dataProvider = experience.dataProvider;
    this.eventHub = experience.eventHub;
    this.initializeDataStructures();
    this.setupInitialData();
    this.setupEventListeners();
    
    // Check if user is already authenticated (handles case where auth:login fired before this)
    this.checkAndFetchUserData();
    
    DataVault.instance = this;
  }

  cleanup() {
    log("Cleaning up DataVault: Clearing all cached data and removing event listeners.");
  
    // Clear stored data
    this.groups.clear();
    this.categorytables.clear();
    this.formFactors.clear();
    this.parts.clear();
    this.user = null;
    this.designs = [];
  
    // Remove event listeners
    this.eventHub.off("requestPart");
    this.eventHub.off("requestCategory");
    this.eventHub.off("requestFormFactor");
    this.eventHub.off("requestGroup");
    this.eventHub.off("auth:login");
  
    log("DataVault cleanup complete.");
  }

  /**
   * Retrieves the singleton instance of DataVault.
   * @param {Object} experience - The experience object containing eventHub and dataProvider.
   * @returns {DataVault} The singleton instance of DataVault.
   */
  static getInstance(experience) {
    if (!DataVault.instance) {
      DataVault.instance = new DataVault(experience);
    }
    return DataVault.instance;
  }

  /**
   * Initializes the internal data structures for groups, category tables, form factors, and parts.
   */
  initializeDataStructures() {
    this.groups = new Map();
    this.categorytables = new Map();
    this.formFactors = new Map();
    this.parts = new Map();
    this.fullSearchTable = null;
    this.searchResults = new Map();
    this.user = null; // Store single user object
    this.designs = []; // Array of user's saved designs
  }

  /**
   * Sets up initial data by fetching groups, category tables, form factors, and parts.
   */
  async setupInitialData() {
    try {
      this.fetchSearchTable();
      this.fetchGroups();
      await this.fetchCategoryTables();
      this.fetchFormFactors();
      await this.fetchParts();
      this.updateFormFactorsWithLayer();
      this.eventHub.emit("loading-finished");
      this.eventHub.emit("check-xmd-cache");
    } catch (error) {
      console.error("Error during initial data setup:", error);
    }
  }

  /**
   * Fetches groups data and stores it in the groups Map.
   */
  async fetchGroups() {
    try {
      const groups = await this.dataProvider.getList("admin/groups");
      groups.forEach((group) => this.groups.set(group.id, group));
      log("Groups data is now available.");
    } catch (error) {
      console.error("Failed to fetch groups:", error);
    }
  }

  /**
   * Fetches category tables data and stores it in the categorytables Map.
   */
  async fetchCategoryTables() {
    console.time("Fetch CategoryTables Time");
    try {
      const categoryTables = await this.dataProvider.getList("categorytables");
      categoryTables.forEach((category) =>
        this.categorytables.set(category.id, category)
      );
      console.log("CategoryTables data is now available.", categoryTables);
    } catch (error) {
      console.error("Failed to fetch category tables:", error);
    } finally {
      console.timeEnd("Fetch CategoryTables Time");
    }
    log("category tables map", this.categorytables);
  }

  /**
   * Processes form factors from category tables and stores them in the formFactors Map.
   */
  fetchFormFactors() {
    try {
      // Ensure category tables have been loaded
      if (this.categorytables && this.categorytables.size > 0) {
        this.categorytables.forEach((category) => {
          const formFactors = category.formFactors;
          if (formFactors && formFactors.length > 0) {
            formFactors.forEach((ff) => {
              this.formFactors.set(ff.ffIdx, ff); // Key the map by ffId
            });
          }
        });
        log("Form Factors data is now available from category tables.");
        this.requestFormFactorImages();
      } else {
        log(
          "Category tables data is not available. Cannot extract form factors."
        );
      }
    } catch (error) {
      console.error(
        "Failed to process form factors from category tables:",
        error
      );
    }
  }

  /**
   * Updates form factors with layer information based on category tables.
   */
  updateFormFactorsWithLayer() {
    try {
      this.formFactors.forEach((formFactor, ffIdx) => {
        // Iterate over each category to find the corresponding pnToffId
        this.categorytables.forEach((category) => {
          const matchingPnToffId = category.pnToffId.find(
            (pnToff) => pnToff.ffId === ffIdx
          );
          if (matchingPnToffId) {
            // Add the layer to the formFactor
            formFactor.layer = matchingPnToffId.layer;
            this.formFactors.set(ffIdx, formFactor); // Update the map
          }
        });
      });
      log("Form Factors updated with layer information.");
    } catch (error) {
      console.error(
        "Failed to update form factors with layer information:",
        error
      );
    }
  }

  /**
   * Fetches parts data and stores it in the parts Map.
   */
  async fetchParts() {
    try {
      let time = Date.now();
      console.log("fetching parts");
      const parts = await this.dataProvider.getList("parts");
      parts.forEach((part) => this.parts.set(part.id, part));
      let endTime = Date.now();
      let duration = (endTime - time) / 1000;
      console.log("All parts2 data is now available.", duration);
    } catch (error) {
      console.error("Failed to fetch parts:", error);
    }
  }

  // Helper method to fetch a part by ID and store it in the parts Map


  /**
   * Fetches a part by its ID and stores it in the parts Map.
   * @param {string} partId - The ID of the part to fetch.
   */
  async fetchPartById(partId) {
    try {
      const part = await this.dataProvider.getOne("parts", partId);
      this.parts.set(partId, part);
      log(`Part data fetched for partId: ${partId}`);
    } catch (error) {
      console.error(`Failed to fetch part with id: ${partId}`, error);
    }
  }

  /**
   * Fetches user data from the API and stores it in the user property.
   * This is called automatically when the user authenticates via the auth:login event.
   */
  async fetchUserData() {
    try {
      log("Fetching user data from management/user endpoint...");
      
      // Small delay to ensure token is fully ready
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Use DataProvider.fetch() directly since this is a special endpoint
      // that doesn't follow the standard getOne() pattern (no ID needed)
      const user = await this.dataProvider.fetch("management/user", "GET");
      
      if (user && typeof user === 'object' && user !== null && !Array.isArray(user) && Object.keys(user).length > 0) {
        // Normalize field names to handle API variations (e.g., phone_number vs phone)
        const normalizedUser = this.normalizeUserData(user);
        this.user = normalizedUser;
        log("User data fetched and cached:", normalizedUser);
        log("Original API response had these phone-related fields:", {
          phone: user.phone,
          phone_number: user.phone_number,
          telephone: user.telephone,
        });
        this.eventHub.emit("userData", normalizedUser); // Emit for other components
      } else {
        log("User data is empty, null, or invalid format. Response:", user);
        console.warn("User data fetch returned empty or invalid response:", user);
      }
    } catch (error) {
      console.error("Failed to fetch user data:", error);
      console.error("Error details:", {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      // Don't throw - allow app to continue even if user fetch fails
    }
  }

  /**
   * Normalizes user data from API to handle field name variations.
   * Ensures consistent field names regardless of API response format.
   * @param {Object} user - Raw user data from API.
   * @returns {Object} Normalized user data with consistent field names.
   */
  normalizeUserData(user) {
    if (!user || typeof user !== 'object') {
      return user;
    }

    const normalized = { ...user };

    // Normalize phone field - check multiple possible field names
    if (!normalized.phone) {
      normalized.phone = user.phone_number || user.telephone || user.Phone || user.PHONE || '';
    }

    // Normalize other fields that might have variations
    // first_name / firstName
    if (!normalized.first_name && user.firstName) {
      normalized.first_name = user.firstName;
    }
    // last_name / lastName
    if (!normalized.last_name && user.lastName) {
      normalized.last_name = user.lastName;
    }

    log("Normalized user data:", {
      original_phone_fields: {
        phone: user.phone,
        phone_number: user.phone_number,
        telephone: user.telephone,
      },
      normalized_phone: normalized.phone,
    });

    return normalized;
  }

  /**
   * Fetches designs data from the API and stores it in the designs array.
   * This is called automatically when the user authenticates via the auth:login event.
   * Does not call the API when the user is not logged in.
   */
  async fetchDesigns() {
    const loginManager = this.experience?.LoginManager;
    const isLoggedIn = loginManager && (typeof loginManager.isAuthenticated === "function" ? loginManager.isAuthenticated() : loginManager.authenticated);
    if (!isLoggedIn) {
      log("User not logged in — skipping design list fetch");
      this.designs = [];
      this.eventHub.emit("designsData", []);
      return;
    }

    try {
      log("Fetching designs from design endpoint...");

      // Small delay to ensure token is fully ready
      await new Promise(resolve => setTimeout(resolve, 100));

      // Only list active designs for the current user (locked designs are view-only via URL)
      const designs = await this.dataProvider.getList({
        resource: "design",
        filter: { status: "active" },
        range: `[0,${this.dataProvider.limit - 1}]`,
        sort: `["createdAt","DESC"]`
      });
      
      // Handle different response structures
      // API might return { data: [...] } or just [...]
      let designsArray = Array.isArray(designs) ? designs : (designs?.data || designs?.items || []);
      // Ensure we never show locked designs in the list (client-side safeguard)
      if (Array.isArray(designsArray)) {
        designsArray = designsArray.filter((d) => d.status !== "locked");
      }
      
      if (Array.isArray(designsArray)) {
        this.designs = designsArray;
        log(`Designs fetched and cached: ${designsArray.length} designs`);
        this.eventHub.emit("designsData", designsArray); // Emit for other components
      } else {
        log("Designs data is not an array. Response:", designs);
        console.warn("Designs fetch returned invalid format:", designs);
        this.designs = [];
      }
    } catch (error) {
      console.error("Failed to fetch designs:", error);
      console.error("Error details:", {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      // Don't throw - allow app to continue even if designs fetch fails
      this.designs = [];
    }
  }

  /**
   * Checks if user is already authenticated and fetches user data if so.
   * This handles the case where auth:login fired before DataVault was initialized.
   */
  async checkAndFetchUserData() {
    try {
      // Check if LoginManager exists and user is authenticated
      const loginManager = this.experience?.LoginManager;
      if (loginManager && loginManager.authenticated) {
        log("User already authenticated, fetching user data...");
        await this.fetchUserData();
        // Designs are only fetched when explicitly requested (e.g., button press)
      } else {
        log("User not authenticated yet, will fetch when auth:login event fires");
      }
    } catch (error) {
      log("Error checking authentication status:", error);
      // Don't throw - allow app to continue
    }
  }

  /**
   * Sets up event listeners for data requests and updates.
   */
  setupEventListeners() {
    this.eventHub.on("requestPart", async (partId) => {
      log(`Received requestPart event for partId: ${partId}`);
      const part = await this.fetchDataIfNeeded("parts", partId);
      log(`Emitting partData event for partId: ${partId}`, part);
      this.eventHub.emit("partData", part);
    });

    this.eventHub.on("requestCategory", async (categoryId) => {
      log(`Received requestCategory event for categoryId: ${categoryId}`);
      const category = await this.fetchDataIfNeeded(
        "categorytables",
        categoryId
      );
      log(
        `Emitting categoryData event for categoryId: ${categoryId}`,
        category
      );
      this.eventHub.emit("categoryData", category);
    });

    this.eventHub.on("requestFormFactor", async (formFactorId) => {
      log(`Received requestFormFactor event for formFactorId: ${formFactorId}`);
      const formFactor = await this.fetchDataIfNeeded(
        "formFactors",
        formFactorId
      );
      log(
        `Emitting formFactorData event for formFactorId: ${formFactorId}`,
        formFactor
      );
      this.eventHub.emit("formFactorData", formFactor);
    });

    this.eventHub.on("requestGroup", async (groupId) => {
      log(`Received requestGroup event for groupId: ${groupId}`);
      const group = await this.fetchDataIfNeeded("groups", groupId);
      log(`Emitting groupData event for groupId: ${groupId}`, group);
      this.eventHub.emit("groupData", group);
    });

    // Listen for authentication events to fetch user data
    // Designs are only fetched when explicitly requested (e.g., button press)
    this.eventHub.on("auth:login", async (authData) => {
      log("Received auth:login event, fetching user data");
      await this.fetchUserData();
    });
  }

  /**
   * Fetches data from the provider if not cached, and caches it.
   * @param {string} resource - The resource name (e.g., parts, groups).
   * @param {string} id - The ID of the resource to fetch.
   * @returns {Object|null} The fetched data or null if not found or an error occurs.
   */
  async fetchDataIfNeeded(resource, id) {
    // Return cached data if it exists
    if (this[resource].has(id)) {
      log(`found in cache ${resource} with id: ${id}`);
      return this[resource].get(id);
    }
  
    log(`not found in cache ${resource} with id: ${id}`);
  
    try {
      if (resource === "parts") {
        log(`Getting presigned URL for part ID: ${id}`);
        const urls = await this.dataProvider.getPresignedUrlsForParts([id]);
  
        if (!urls || urls.length === 0) {
          throw new Error(`No presigned URL returned for part: ${id}`);
        }
  
        const presignedUrl = urls[0];
  
        log(`Fetching part data from presigned URL: ${presignedUrl}`);
        const response = await fetch(presignedUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch part from presigned URL`);
        }
  
        const part = await response.json(); // or `.blob()` if binary, adjust accordingly
        this.parts.set(id, part);
        log(`Fetched and cached part with id: ${id}`, part);
        return part;
      }
  
      // Fallback for other resources
      log("fetching data from dataprovider");
      const data = await this.dataProvider.getOne(resource, id);
      log("data,", data);
      if (data) {
        this[resource].set(id, data);
        log(`Fetched and cached ${resource} with id: ${id}`, data);
        return data;
      } else {
        log(`No data found for ${resource} with id: ${id}`);
        return null;
      }
    } catch (error) {
      console.error(`Error fetching ${resource} with id: ${id}`, error);
      return null;
    }
  }
  

  /**
   * Requests images for all groups.
   */
  requestGroupImages() {
    const groupImages = [];
    this.groups.forEach((group) => {
      group.categories.forEach((category) => {
        if (category["Category Image"]) {
          groupImages.push(category["Category Image"]);
        }
      });
    });
    log("Emitting getImages with paths:", groupImages.length);
    this.eventHub.emit("getImages", groupImages);
  }

  /**
   * Requests images for all form factors with type HYB.
   */
  requestFormFactorImages() {
    const formFactorImages = [];
    this.formFactors.forEach((formFactor) => {
      formFactor.images.forEach((image) => {
        if (image.imageType === "HYB") {
          formFactorImages.push(image.imageURL);
        }
      });
    });

    log("Emitting getImages with paths formfactors:", formFactorImages.length);
    this.eventHub.emit("getImages", formFactorImages);
  }

  async fetchSearchTable() {
    try {
      const headers = await this.dataProvider._getAuthHeaders();
      const response = await fetch(partsTableUrl, {
        method: "POST",
        headers,
        body: JSON.stringify({ searchBy: "", page: 1, limit: 10000 }),
      });

      if (!response.ok) throw new Error(`Failed to fetch search table`);
      const json = await response.json();

      this.fullSearchTable = json;
      log(`Cached full search table with ${json?.values?.length ?? 0} entries`);
    } catch (err) {
      console.error("Error fetching full search table:", err);
    }
  }

  cacheSearchResults(searchBy, page, result) {
    const key = `${searchBy.toLowerCase()}::${page}`;
    this.searchResults.set(key, result);
  }

  getCachedSearchResults(searchBy, page) {
    const key = `${searchBy.toLowerCase()}::${page}`;
    return this.searchResults.get(key);
  }

  hasCachedSearchResults(searchBy, page) {
    const key = `${searchBy.toLowerCase()}::${page}`;
    return this.searchResults.has(key);
  }

  getSearchResults({ searchBy = "", page = 1, limit = 10 }) {
    if (!this.fullSearchTable) return { headers: [], values: [] };

    const query = searchBy.toLowerCase();
    const allRows = this.fullSearchTable.values.filter((row) =>
      row.some((cell) => String(cell).toLowerCase().includes(query))
    );

    const pageStart = (page - 1) * limit;
    const pageEnd = pageStart + limit;
    const pageValues = allRows.slice(pageStart, pageEnd);
    const hasMorePages = pageEnd < allRows.length;
    
    return {
      headers: this.fullSearchTable.headers,
      values: pageValues,
      hasMorePages: hasMorePages,
    };
  }

}

export default DataVault;
